/**
 * The icon scale.
 *
 * Before this existed every call site invented its own number — 12, 14, 16, 18,
 * 20, 24, 28, and a handful of `"16px"` strings that only worked because SVG
 * tolerates units in a width attribute. Eight sizes across three notations is
 * not a system, it is a habit. These are the six steps the product actually
 * needs, each tied to the place it belongs:
 *
 *   xs  12  dense inline affordances — chips, table cell actions, tiny buttons
 *   sm  14  inline beside body text, small buttons
 *   md  16  the default control size — toolbars, medium buttons
 *   lg  20  standalone controls and list rows (the component default)
 *   xl  24  section headers, large buttons
 *   2xl 32  bubble marks — a file, a location, a failed attachment
 *   3xl 40  drop-zone and overlay marks
 *   4xl 48  empty-state marks
 *
 * Above 24 the steps are the 8-unit grid the glyphs are drawn on, so a shape
 * scales by whole units and never lands a stroke on a half pixel. 18 and 28 do
 * land on one at common zoom levels, which is why they are not steps.
 */
export const ICON_SIZES = Object.freeze({
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 48,
});

const SIZE_STEPS = Object.freeze(Object.values(ICON_SIZES));

const DEFAULT_SIZE = ICON_SIZES.lg;

/**
 * Resolves whatever a call site passed into a step on the scale.
 *
 * Accepts a token (`"md"`), a number (`16`), or a numeric string with or
 * without a unit (`"16"`, `"16px"`). Anything off-scale snaps to the nearest
 * step, ties resolving downward — an icon that shrinks by two pixels stays
 * inside the box its caller reserved, one that grows can overflow it.
 *
 * @param {string|number} size
 * @returns {number} a pixel value that is always one of ICON_SIZES
 */
export const resolveIconSize = size => {
  if (typeof size === 'string' && size in ICON_SIZES) {
    return ICON_SIZES[size];
  }

  const parsed = parseFloat(size);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_SIZE;
  }

  return SIZE_STEPS.reduce((closest, step) => {
    const isCloser = Math.abs(step - parsed) < Math.abs(closest - parsed);
    return isCloser ? step : closest;
  }, SIZE_STEPS[0]);
};

/**
 * Icons whose meaning is "the direction you read in", and which therefore have
 * to face the other way in Arabic.
 *
 * Names carry no `-outline`/`-solid` suffix: FluentIcon appends the style
 * itself, so both variants of a name are covered by one entry.
 *
 * What is deliberately NOT here matters as much as what is:
 *
 * - `arrow-clockwise`, `arrow-rotate-clockwise`, `arrow-rotate-counter-clockwise`
 *   — a rotation direction is a fact about the rotation, not about reading
 *   order. Mirroring "refresh" would say "rotate the other way".
 * - `play-circle`, `microphone-play` — media transport is universally
 *   left-to-right, in every locale, on every device. Mirroring play is wrong.
 * - `arrow-up`, `arrow-download`, `upload`, `arrow-sort` — vertical.
 * - `arrow-swap` — a symmetric double arrow; mirroring is a no-op.
 * - `arrow-trending-lines` — it labels a chart, and chart axes do not mirror.
 */
export const RTL_MIRRORED_ICONS = Object.freeze([
  'arrow-chevron-left',
  'arrow-chevron-right',
  'arrow-expand',
  'arrow-redo',
  'arrow-reply',
  'arrow-right',
  'arrow-right-import',
  'arrow-up-right',
  'chevron-left',
  'chevron-right',
  'open',
  'pane-close',
  'pane-open',
  'panel-contract',
  'panel-sidebar',
  'send',
  'send-clock',
  'share',
  'sign-out',
]);

const MIRRORED_LOOKUP = new Set(RTL_MIRRORED_ICONS);

/**
 * @param {string} icon icon name without its style suffix
 * @returns {boolean} whether the glyph must face the other way in RTL
 */
export const isDirectionalIcon = icon => MIRRORED_LOOKUP.has(icon);

/**
 * Stroke width for the stroke-drawn icon libraries (Lucide). Fluent icons are
 * filled paths and carry their own optical weight, so this applies only to the
 * outline set — one number, so the two families read as one.
 */
export const STROKE_WIDTH = 1.66667;
